import type { TranslationObj } from "$/utils/lang"

// rest types --start
type TCoachingChatConversationStatusDto = "completed" | "in_progress"

type TCoachingChatMessageAuthor = "assistant" | "user"

enum ECoachingChatMessageAuthor {
    AI = "assistant",
    User = "user"
}

type TCoachingChatMessageTransport = "rest" | "websocket"

enum ECoachingChatMessageTransport {
    Sockets = "websocket",
    Rest = "rest"
}

interface ICoachingChatMessageQueryDto {
    id: number
    conversation_id: number
    author_role: TCoachingChatMessageAuthor
    transport: TCoachingChatMessageTransport | null
    message: string
    conversation: Omit<ICoachingChatConversationQueryDto, "messages">
}

interface ICoachingChatMessageQueryModel {
    id: number
    conversationId: number
    author: ECoachingChatMessageAuthor
    transport: ECoachingChatMessageTransport | null
    message: string
    conversation: Omit<ICoachingChatConversationQueryModel, "messages" | "isMatrixFeedbackCompleted">
}

type ICoachingChatConversationSummaryCardQueryDto = Record<string, string>

interface ICoachingChatConversationSummaryCardQueryModel {
    title: string
    content: string
    completedAt?: string
}

interface ICoachingChatConversationQueryDto {
    completed_stamp: string
    id: number
    status: TCoachingChatConversationStatusDto
    summary_cards: ICoachingChatConversationSummaryCardQueryDto[]
    messages: ICoachingChatMessageQueryDto[]
    is_matrix_feedback_completed: boolean
}

interface ICoachingChatConversationQueryModel {
    completedStamp: string
    id: number
    status: TCoachingChatConversationStatusDto
    summaryCards: ICoachingChatConversationSummaryCardQueryModel[]
    messages: ICoachingChatMessageQueryModel[]
    isMatrixFeedbackCompleted: boolean
}

interface ICoachingChatNewMessagesErrorQueryDto {
    message_id: number
    completion_type: string
    moderation_id?: number
    conversation_id?: number
    msg?: string
}

interface ICoachingChatNewMessagesQueryDto {
    errors: ICoachingChatNewMessagesErrorQueryDto[] | []
    messages: ICoachingChatMessageQueryDto[]
}

interface ICoachingChatNewMessagesErrorQueryModel {
    messageId?: number
    moderationId?: number
    message?: string
    conversationId?: number
}

interface ICoachingChatNewMessagesQueryModel {
    errors: ICoachingChatNewMessagesErrorQueryModel[] | []
    messages: ICoachingChatMessageQueryModel[]
}

interface ICoachingChatDisclaimerQueryDto {
    id: number
    text: TranslationObj
}
// rest types --end
// -------
// socket types --start
enum ECoachingChatSocketErrorReason {
    Guardrail = "input_moderated",
    GuardrailInternalError = "moderation_internal_error",
    DisclaimerRequired = "ai_disclaimer_required",
    SchemaValidationError = "schema_validation_error",
    ValidationError = "minimal_shape_validation_failed",
    TemplateCorrupted = "prompt_build_error",
    InvalidPayload = "invalid_payload",
    SummaryRequired = "summary_required",
    ParticipantNotFound = "participant_not_found",
    ConversationFailed = "conversation_failed",
    RestartNotAllowed = "restart_not_allowed",
    ConversationExists = "conversation_already_exists",
    ConversationCompleted = "conversation_completed",
    ConversationNotFound = "conversation_not_found",
    AlreadyInProgress = "already_in_progress",
    JsonDecodeError = "json_decode_error",
    EmptyResponse = "empty_response",
    StreamingIdle = "openai_timeout_idle",
    StreamingFatalIdle = "openai_timeout_total",
    FirstChunkMissing = "openai_timeout_first_chunk",
    GeneralError = "openai_error",
    Connection = "openai_connection_error",
    RateLimit = "openai_rate_limit"
}

type _TCoachingChatSocketErrorEventResponseShared = {
    code: ECoachingChatSocketErrorReason
    conversation_id: number | null
    conversation_status: "in_progress" | "failed" | "completed" | null
    // cm_join: the server answers a join on an already-finished conversation with
    // conversation_completed carrying this phase
    phase: "cm_send" | "cm_start" | "cm_restart" | "cm_join" | "streaming"
    retryable: boolean
    suggested_action: "cm_send" | "cm_start" | "cm_restart"
    suggested_mode: "resume"
    participant_message_id: number | null
    message: string | null
}

type TCoachingChatSocketErrorEventResponse =
    | ({
          code: ECoachingChatSocketErrorReason.JsonDecodeError
          raw_response: string | object
      } & _TCoachingChatSocketErrorEventResponseShared)
    | ({
          code: ECoachingChatSocketErrorReason.Guardrail | ECoachingChatSocketErrorReason.GuardrailInternalError
          moderation: { message_id: number; moderation_id: number }
          client_message_uuid: string
          tab_uuid: string
      } & _TCoachingChatSocketErrorEventResponseShared)
    | ({
          code:
              | ECoachingChatSocketErrorReason.EmptyResponse
              | ECoachingChatSocketErrorReason.TemplateCorrupted
              | ECoachingChatSocketErrorReason.DisclaimerRequired
              | ECoachingChatSocketErrorReason.ParticipantNotFound
              | ECoachingChatSocketErrorReason.InvalidPayload
              | ECoachingChatSocketErrorReason.GeneralError
              | ECoachingChatSocketErrorReason.Connection
              | ECoachingChatSocketErrorReason.RateLimit
              | ECoachingChatSocketErrorReason.SchemaValidationError
              | ECoachingChatSocketErrorReason.ValidationError
              | ECoachingChatSocketErrorReason.SummaryRequired
              | ECoachingChatSocketErrorReason.ConversationFailed
              | ECoachingChatSocketErrorReason.RestartNotAllowed
              | ECoachingChatSocketErrorReason.ConversationExists
              | ECoachingChatSocketErrorReason.ConversationCompleted
              | ECoachingChatSocketErrorReason.ConversationNotFound
              | ECoachingChatSocketErrorReason.StreamingIdle
              | ECoachingChatSocketErrorReason.StreamingFatalIdle
              | ECoachingChatSocketErrorReason.FirstChunkMissing
              | ECoachingChatSocketErrorReason.AlreadyInProgress
      } & _TCoachingChatSocketErrorEventResponseShared)

enum ECoachingChatSocketEmitterEventName {
    StartConversation = "cm_start",
    JoinConversation = "cm_join",
    RestartConversation = "cm_restart",
    Message = "cm_send"
}

enum ECoachingChatSocketListenerEventName {
    StartConversation = "cm:started",
    History = "history",
    Ready = "ready",
    Message = "cm:message",
    Error = "cm:error",
    Broadcast = "user_line",
    ConversationRestarted = "cm:restarted"
}

type TCoachingChatSocketStartConversationEventPayload = {
    chapter_progress: number
    component: number
    auto_stream?: boolean // default true
    prompt_override_id?: number
    seed_questions_override?: string[]
}

type TCoachingChatSocketStartConversationEventResponse = {
    auto_stream: boolean
    conversation_id: number
    progress_id: number
    prompt_id: number
}

type TCoachingChatSocketRestartConversationEventPayload = {
    conversation_id: number
    mode: "resume"
    participant_message_id?: number
}

type TCoachingChatSocketHistoryEventData = {
    role: TCoachingChatMessageAuthor
    content: string
    transport: TCoachingChatMessageTransport // currently backend omits setting this field at all
    msg_id: number
}

type TCoachingChatSocketHistoryEventResponse = TCoachingChatSocketHistoryEventData[]

type TCoachingChatSocketReadyEventResponse = { conversation_id: number }

type TCoachingChatSocketConversationRestartedEventResponse = {
    conversation_id: number
    mode: "resume"
    participant_message_id: number
}

type TCoachingChatSocketJoinConversationEventPayload = { conversation_id: number }

type TCoachingChatSocketMessageEventPayload = {
    conversation_id: number
    text: string
    tab_uuid: string
    client_message_uuid: string
    transport: TCoachingChatMessageTransport
}

type TCoachingChatSocketMessageEventResponse = {
    role: TCoachingChatMessageAuthor
    content: string // stringified {is_chat_complete: boolean, chat_stage: number, message: string, chat_summary: [] }
    done: boolean
    conversation_id: number
    assistant_message_id?: number
    participant_message_id: number
    moderation_id?: number | null
}

type TCoachingChatSocketBroadcastEventResponse = {
    client_message_uuid: string
    conversation_id: number
    tab_uuid: string
    participant_message_id: number
    text: string
}

interface ICoachingChatSocketServerEvents {
    [ECoachingChatSocketListenerEventName.StartConversation]: (
        data: TCoachingChatSocketStartConversationEventResponse
    ) => void
    [ECoachingChatSocketListenerEventName.History]: (data: TCoachingChatSocketHistoryEventResponse) => void
    [ECoachingChatSocketListenerEventName.Ready]: (data: TCoachingChatSocketReadyEventResponse) => void
    [ECoachingChatSocketListenerEventName.Message]: (data: string) => void
    [ECoachingChatSocketListenerEventName.Error]: (data: TCoachingChatSocketErrorEventResponse) => void
}

interface ICoachingChatSocketClientEvents {
    [ECoachingChatSocketEmitterEventName.StartConversation]: (
        payload: TCoachingChatSocketStartConversationEventPayload
    ) => void
    [ECoachingChatSocketEmitterEventName.RestartConversation]: (
        payload: TCoachingChatSocketRestartConversationEventPayload
    ) => void
    [ECoachingChatSocketEmitterEventName.JoinConversation]: (
        payload: TCoachingChatSocketJoinConversationEventPayload
    ) => void
    [ECoachingChatSocketEmitterEventName.Message]: (payload: TCoachingChatSocketMessageEventPayload) => void
}
// socket types --end

export type {
    // rest
    ICoachingChatMessageQueryModel,
    TCoachingChatMessageTransport,
    ICoachingChatMessageQueryDto,
    ICoachingChatConversationQueryDto,
    ICoachingChatConversationQueryModel,
    TCoachingChatConversationStatusDto,
    TCoachingChatMessageAuthor,
    ICoachingChatNewMessagesQueryDto,
    ICoachingChatNewMessagesErrorQueryDto,
    ICoachingChatNewMessagesQueryModel,
    ICoachingChatNewMessagesErrorQueryModel,
    ICoachingChatConversationSummaryCardQueryDto,
    ICoachingChatConversationSummaryCardQueryModel,
    ICoachingChatDisclaimerQueryDto,
    // sockets
    TCoachingChatSocketErrorEventResponse,
    TCoachingChatSocketJoinConversationEventPayload,
    TCoachingChatSocketConversationRestartedEventResponse,
    TCoachingChatSocketStartConversationEventPayload,
    TCoachingChatSocketStartConversationEventResponse,
    TCoachingChatSocketRestartConversationEventPayload,
    TCoachingChatSocketHistoryEventData,
    TCoachingChatSocketHistoryEventResponse,
    TCoachingChatSocketReadyEventResponse,
    TCoachingChatSocketMessageEventPayload,
    TCoachingChatSocketMessageEventResponse,
    TCoachingChatSocketBroadcastEventResponse,
    ICoachingChatSocketServerEvents,
    ICoachingChatSocketClientEvents
}
export {
    ECoachingChatMessageAuthor,
    ECoachingChatMessageTransport,
    ECoachingChatSocketListenerEventName,
    ECoachingChatSocketEmitterEventName,
    ECoachingChatSocketErrorReason
}
